import { useGameStore } from '@hollowveil/shared';

// Visual effect definitions using CSS filters
export const VISUAL_EFFECTS = {
  // No effect
  none: { name: 'Aucun', description: 'Pas d\'effet', filter: '', overlay: '' },

  // Color effects
  sepia: { name: 'Sépia', description: 'Style photo ancienne', filter: 'sepia(80%)', overlay: '' },
  grayscale: { name: 'Noir & Blanc', description: 'Désaturation totale', filter: 'grayscale(100%)', overlay: '' },
  vibrant: { name: 'Vibrant', description: 'Couleurs saturées', filter: 'saturate(150%) contrast(110%)', overlay: '' },
  cold: { name: 'Froid', description: 'Tons bleutés', filter: 'saturate(90%) hue-rotate(20deg)', overlay: '' },
  warm: { name: 'Chaud', description: 'Tons orangés', filter: 'saturate(110%) hue-rotate(-15deg)', overlay: '' },

  // Brightness/Contrast effects
  bright: { name: 'Lumineux', description: 'Plus de luminosité', filter: 'brightness(120%)', overlay: '' },
  dark: { name: 'Sombre', description: 'Moins de luminosité', filter: 'brightness(80%)', overlay: '' },
  high_contrast: { name: 'Contraste Fort', description: 'Contraste accentué', filter: 'contrast(130%)', overlay: '' },
  low_contrast: { name: 'Contraste Faible', description: 'Contraste réduit', filter: 'contrast(80%)', overlay: '' },

  // Atmosphere effects
  vintage: { name: 'Vintage', description: 'Style années 70', filter: 'sepia(30%) contrast(90%) brightness(95%)', overlay: 'vignette' },
  noir: { name: 'Film Noir', description: 'Style cinéma noir', filter: 'grayscale(100%) contrast(130%) brightness(90%)', overlay: 'vignette' },
  dreamy: { name: 'Rêveur', description: 'Effet onirique', filter: 'brightness(105%) contrast(95%) saturate(110%) blur(0.5px)', overlay: '' },

  // Special effects
  invert: { name: 'Inversé', description: 'Couleurs inversées', filter: 'invert(100%)', overlay: '' },
  neon: { name: 'Néon', description: 'Style cyberpunk', filter: 'saturate(200%) contrast(120%) brightness(110%)', overlay: '' },
  underwater: { name: 'Sous-marin', description: 'Effet aquatique', filter: 'hue-rotate(180deg) saturate(80%) brightness(95%)', overlay: '' },
  sunset: { name: 'Coucher de soleil', description: 'Tons chauds dorés', filter: 'sepia(20%) saturate(130%) hue-rotate(-10deg)', overlay: 'vignette-warm' },

  // New effects
  vignette: { name: 'Vignette', description: 'Bords assombris', filter: '', overlay: 'vignette' },
  blur: { name: 'Flou léger', description: 'Effet de rêve', filter: 'blur(1px)', overlay: '' },
  crt: { name: 'CRT', description: 'Écran cathodique', filter: 'contrast(110%) brightness(95%)', overlay: 'crt' },
  scanlines: { name: 'Scanlines', description: 'Lignes de balayage', filter: '', overlay: 'scanlines' },
  glitch: { name: 'Glitch', description: 'Distorsion numérique', filter: '', overlay: 'glitch' },
  chromatic: { name: 'Aberration', description: 'Aberration chromatique', filter: '', overlay: 'chromatic' },
  pixelate: { name: 'Pixelisé', description: 'Style rétro 8-bit', filter: '', overlay: 'pixelate' },
  night_vision: { name: 'Vision nocturne', description: 'Vert phosphorescent', filter: 'brightness(150%) contrast(120%) saturate(0%) sepia(100%) hue-rotate(70deg)', overlay: 'scanlines' },
  posterize: { name: 'Postérisation', description: 'Couleurs réduites', filter: 'contrast(200%) saturate(150%)', overlay: '' },
  vhs: { name: 'VHS', description: 'Style cassette vidéo', filter: 'contrast(110%) saturate(130%)', overlay: 'vhs' },
  horror: { name: 'Horreur', description: 'Ambiance terrifiante', filter: 'contrast(120%) brightness(80%) saturate(70%)', overlay: 'vignette-dark' },
  fairy: { name: 'Féerique', description: 'Ambiance magique', filter: 'brightness(110%) saturate(120%) hue-rotate(10deg)', overlay: 'sparkle' },
} as const;

export type VisualEffectName = keyof typeof VISUAL_EFFECTS;

// This component returns null - effects are applied via CSS
export function PostEffects() {
  return null;
}

// Hook to get current effect filter
export function useVisualEffectFilter(): string {
  const visualEffect = useGameStore((state) => state.visualEffect);

  if (!visualEffect || visualEffect === 'none') {
    return '';
  }

  const effect = VISUAL_EFFECTS[visualEffect as VisualEffectName];
  return effect?.filter || '';
}

// Hook to get current overlay class
export function useVisualEffectOverlay(): string {
  const visualEffect = useGameStore((state) => state.visualEffect);

  if (!visualEffect || visualEffect === 'none') {
    return '';
  }

  const effect = VISUAL_EFFECTS[visualEffect as VisualEffectName];
  return effect?.overlay || '';
}
